import { inputTypes } from "../../constants/commonConstants";
import { convertLengthValue } from "../../utils/unitConversion";

// No need to destructure from commonConstant since inputTypes is directly imported

const validatePositiveInteger = (_, value) => {
  if (value && !Number.isInteger(Number(value))) {
    return Promise.reject("Enter a valid integer");
  }
  if (Number(value) == 0) {
    return Promise.reject("Enter a non-zero positive number");
  } else if (Number(value) < 1) {
    return Promise.reject("Enter a positive number");
  }
  return Promise.resolve();
};

// const validatePositiveDecimal = (value) => {
//   const floatValue = parseFloat(value);

//   if (value && isNaN(parseFloat(floatValue))) {
//     return Promise.reject("Please enter a valid number");
//   }
//   if (floatValue == 0) {
//     return Promise.reject("Enter a non-zero positive number");
//   } else if (floatValue < 1) {
//     return Promise.reject(`Please enter positive value`);
//   }
//   return Promise.resolve();
// };

const validateEndDate = (startDate, endDate) => {
  if (endDate && startDate) {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    if (start > end) {
      return Promise.reject("Start Date should not be later than End Date");
    }
  }
  return Promise.resolve();
};

// Helper function to get unique values from projects
const getUniqueValuesFromProjects = (projects, fieldName) => {
  if (!projects) return [];

  const values = projects
    .map((project) => {
      switch (fieldName) {
        case "wallThickness":
          return project.pipe_thickness?.toString();
        case "pipeDiameter":
          return project.pipe_diameter?.toString();
        case "pipeMaterial":
          return project.pipe_material;
        default:
          return null;
      }
    })
    .filter((value) => value !== null && value !== undefined && value !== "");

  return [...new Set(values)].sort();
};

const createProjectFormConfig = (projects = [], currentProject = null) => ({
  wallThickness: {
    type: inputTypes.editableSelect,
    label: "projectLabels.wallThickness",
    unitSuffix: currentProject?.units?.wall_thickness ? ` (${currentProject.units.wall_thickness})` : '',
    options: getUniqueValuesFromProjects(projects, "wallThickness"),
    rules: [
      { required: false },
      {
        validator: (_, value) => {
          if (value && isNaN(parseFloat(value))) {
            return Promise.reject("Please enter a valid number");
          }
          if (value && parseFloat(value) <= 0) {
            return Promise.reject("Please enter a positive number");
          }
          return Promise.resolve();
        },
      },
    ],
    tooltip: "projectTooltip.wallThickness",
    allowCustomValue: true,
    placeholder: `Enter wall thickness in ${currentProject?.units?.wall_thickness || 'mm'}`,
  },

  pipeDiameter: {
    type: inputTypes.editableSelect,
    label: "projectLabels.pipeDiameter",
    unitSuffix: currentProject?.units?.pipe_diameter ? ` (${currentProject.units.pipe_diameter})` : '',
    options: getUniqueValuesFromProjects(projects, "pipeDiameter"),
    rules: [
      { required: false },
      {
        validator: (_, value) => {
          if (value && isNaN(parseFloat(value))) {
            return Promise.reject("Please enter a valid number");
          }
          if (value && parseFloat(value) <= 0) {
            return Promise.reject("Please enter a positive number");
          }
          return Promise.resolve();
        },
      },
    ],
    tooltip: "projectTooltip.pipeDiameter",
    allowCustomValue: true,
    placeholder: `Enter pipe diameter in ${currentProject?.units?.pipe_diameter || 'mm'}`,
  },

  pipeMaterial: {
    type: inputTypes.editableSelect,
    label: "projectLabels.pipeMaterial",
    options: getUniqueValuesFromProjects(projects, "pipeMaterial"),
    rules: [
      { required: false },
      {
        validator: (_, value) => {
          if (value && value.trim() === "") {
            return Promise.reject("Pipe Material cannot be blank");
          }
          return Promise.resolve();
        },
      },
    ],
    tooltip: "projectTooltip.pipeMaterial",
    allowCustomValue: true,
    placeholder: "Enter pipe material",
  },
});

const projectCreationForm = {
  projectName: {
    type: inputTypes.input,
    label: "projectLabels.projectName",
    rules: [
      { required: true, message: "Project Name is required" },
      {
        validator: (_, value) => {
          if (value && value.trim() === "") {
            return Promise.reject("Project Name cannot be blank");
          }
          return Promise.resolve();
        },
      },
    ],
    tooltip: "projectTooltip.projectName",
  },

  projectNumber: {
    type: inputTypes.input,
    label: "projectLabels.projectNumber",
    rules: [
      { required: true, message: "Project Number is required" },
      {
        validator: (_, value) => {
          if (value && /\s/.test(value)) {
            return Promise.reject(
              "Spaces are not allowed in the project number"
            );
          }
          return Promise.resolve();
        },
      },
    ],
    tooltip: "projectTooltip.projectNumber",
  },

  clientName: {
    type: inputTypes.input,
    label: "projectLabels.clientName",
    rules: [
      { required: true, message: "Client Name is required" },
      {
        validator: (_, value) => {
          if (value && value.trim() === "") {
            return Promise.reject("Client Name cannot be blank");
          }
          return Promise.resolve();
        },
      },
    ],
    tooltip: "projectTooltip.clientName",
  },

  technicianName: {
    type: inputTypes.input,
    label: "projectLabels.technicianName",
    rules: [{ required: false }],
    tooltip: "projectTooltip.technicianName",
  },

  weldsPlanned: {
    type: inputTypes.input,
    label: "projectLabels.weldsPlanned",
    rules: [
      { required: true, message: "Welds Planned is required" },
      {
        validator: (_, value) => validatePositiveInteger(_, value),
      },
    ],
    tooltip: "projectTooltip.weldsPlanned",
  },

  startDate: {
    type: inputTypes.date,
    label: "projectLabels.startDate",
    rules: [
      { required: true, message: "Start Date is required" },
      ({ getFieldValue }) => ({
        validator(_, value) {
          return validateEndDate(value, getFieldValue("endDate"));
        },
      }),
    ],
    tooltip: "projectTooltip.startDate",
  },

  endDate: {
    type: inputTypes.date,
    label: "projectLabels.endDate",
    rules: [
      { required: false },
      ({ getFieldValue }) => ({
        validator(_, value) {
          return validateEndDate(getFieldValue("startDate"), value);
        },
      }),
    ],
    tooltip: "projectTooltip.endDate",
  },

  weldTagging: {
    type: inputTypes.radio,
    label: "projectLabels.weldTagging",
    options: [
      { value: "Manual", label: "Manual" },
      { value: "Automatic", label: "Automatic/Smart" },
    ],
    rules: [{ required: false }],
    tooltip: "projectTooltip.weldTagging",
  },

  weldingSystem: {
    type: inputTypes.checkbox,
    label: "projectLabels.weldingSystem",
    options: [
      { value: "IWM", label: "IWM" },
      { value: "P-600Z", label: "P-600Z" },
      { value: "P-625", label: "P-625" },
      { value: "M-500", label: "M-500" },
    ],
    rules: [
      { required: true, message: "Please select at least one welding system" },
      {
        validator: (_, value) => {
          if (!value || Object.values(value).every((v) => !v)) {
            return Promise.reject("Please select at least one welding system");
          }
          return Promise.resolve();
        },
      },
    ],
    tooltip: "projectTooltip.weldingSystem",
  },

  numHeads: {
    type: inputTypes.number,
    label: "projectLabels.numHeads",
    options: [1, 2, 3, 4, 5],
    rules: [
      {
        required: (form) => form.getFieldValue("weldingSystem")?.iwm,
        message: "Number of heads is required when IWM is selected",
      },
    ],
    tooltip: "projectTooltip.numHeads",
    dependencies: ["weldingSystem"],
    shouldRender: (form) => form.getFieldValue("weldingSystem")?.iwm,
  },

  numStations: {
    type: inputTypes.number,
    label: "projectLabels.numStations",
    options: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    rules: [{ required: false }],
    tooltip: "projectTooltip.numStations",
  },
};

const mapLocalStateToApiRequest = (localState) => {
  return {
    projectName: localState.projectName,
    projectNumber: localState.projectNumber,
    clientName: localState.clientName,
    technicianName: localState.technicianName,
    weldsPlanned: localState.weldsPlanned,
    wallThickness: localState.wallThickness,
    pipeDiameter: localState.pipeDiameter,
    pipeMaterial: localState.pipeMaterial,
    startDate: localState.startDate,
    endDate: localState.endDate,
    weldTagging: localState.weldTagging,
    weldingSystem: localState.weldingSystem,
    numHeads: localState.numHeads,
    numStations: localState.numStations,
  };
};

const transformToApiPayload = (formData) => {
  const { projectDetails, reportConfiguration } = formData;

  // Transform report columns - use API field names directly
  const reportColumns = {
    current: reportConfiguration.reportConfig.current || false,
    volts: reportConfiguration.reportConfig.volts || false,
    travel_speed: reportConfiguration.reportConfig.travel_speed || false,
    true_energy: reportConfiguration.reportConfig.true_energy || false,
    heat: reportConfiguration.reportConfig.heat || false,
    wire_speed: reportConfiguration.reportConfig.wire_speed || false,
    oscillation_width:
      reportConfiguration.reportConfig.oscillation_width || false,
    frequency: reportConfiguration.reportConfig.frequency || false,
    target: reportConfiguration.reportConfig.target || false,
    horizontal_bias: reportConfiguration.reportConfig.horizontal_bias || false,
    total_wire_consumed:
      reportConfiguration.reportConfig.total_wire_consumed || false,
    arc_impedance: reportConfiguration.reportConfig.arc_impedance || false,
    summary_page: reportConfiguration.reportConfig.summary_page || false,
    is_high_low_avg_required:
      reportConfiguration.reportConfig.is_high_low_avg_required || false,
  };

  const units = reportConfiguration.units;

  // Convert thickness and pipe diameter from display units to inches for API
  let convertedWallThickness = projectDetails.wallThickness;
  let convertedPipeDiameter = projectDetails.pipeDiameter;

  if (convertedWallThickness && units.wall_thickness) {
    convertedWallThickness = convertLengthValue(convertedWallThickness, units.wall_thickness, "in");
  }
  if (convertedPipeDiameter && units.pipe_diameter) {
    convertedPipeDiameter = convertLengthValue(convertedPipeDiameter, units.pipe_diameter, "in");
  }

  return {
    project_name: projectDetails.projectName,
    project_number: projectDetails.projectNumber,
    customer_name: projectDetails.clientName,
    start_date: projectDetails.startDate?.format("YYYY-MM-DD"),
    welds_planned: parseInt(projectDetails.weldsPlanned),
    welding_systems: projectDetails.weldingSystem,
    pipe_thickness: parseFloat(convertedWallThickness),
    pipe_diameter: parseFloat(convertedPipeDiameter),
    pipe_material:
      projectDetails.pipeMaterial === "" ? null : projectDetails.pipeMaterial,
    end_date: projectDetails.endDate?.format("YYYY-MM-DD"),
    user_id:
      projectDetails.technicianName === ""
        ? null
        : projectDetails.technicianName,
    num_stations: parseInt(projectDetails.numStations),
    welds_completed: 0,
    report_columns: reportColumns,
    units,
    weldAssignmentType: projectDetails.weldTagging,
    num_heads_iwm: parseInt(projectDetails.numHeads),
  };
};

export {
  projectCreationForm,
  createProjectFormConfig,
  mapLocalStateToApiRequest,
  transformToApiPayload,
};
